import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  IconButton,
  Popover,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ruRU, enUS } from "@mui/x-date-pickers/locales";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import {
  Meeting,
  getMeetingById,
  updateMeeting,
  createMeeting,
  deleteMeeting,
} from "../services/meetingService";
import { User } from "../types";
import ParticipantsAccordion from "../components/Accordions/ParticipantsAccordion";
import ConfirmDialog from "../components/Common/ConfirmDialog";
import Notification from "../components/Common/Notification";
import MeetingView from "../components/MeetingView";
import { MeetingStatusKey, meetingStatusColors } from "../utils/statusColors";
import { DEFAULT_CONFIG } from "../utils/config";
import useAcl from "../hooks/useAcl";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "../utils/draftStorage";
import DraftPromptDialog from "../components/Common/DraftPromptDialog";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("ru");

const DEVICE_TZ = dayjs.tz.guess();

const fromServerToLocal = (iso?: string) =>
  iso ? dayjs.utc(iso).tz(DEVICE_TZ) : null;

const normalizeIsoUtc = (iso?: string) =>
  iso ? dayjs.utc(iso).format("YYYY-MM-DDTHH:mm:ss[Z]") : "";

const toServerUtcIsoStrict = (d?: dayjs.Dayjs | null) =>
  d ? d.tz(DEVICE_TZ).utc().format("YYYY-MM-DDTHH:mm:ss[Z]") : "";

const MeetingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultUserId = user?.id || "fallback-user-id";
  const defaultUserName = user?.name || t("meetingDetail.currentUser");

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [isEditing, setIsEditing] = useState(id === "new");
  const [form, setForm] = useState<Partial<Meeting>>({
    status: "Planned",
    assignedUserId: defaultUserId,
    assignedUserName: defaultUserName,
    usersIds: [],
    usersNames: {},
    usersColumns: {},
    name: "",
    description: "",
    group: "",
    dateStart: "",
    createdById: defaultUserId,
  });
  const [duration, setDuration] = useState<string>("1");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [draftPromptOpen, setDraftPromptOpen] = useState(false);
  const [draftToRestore, setDraftToRestore] = useState<{
    form: Partial<Meeting>;
    duration: string;
  } | null>(null);
  const draftPromptShownRef = useRef(false);

  const draftKey = buildDraftKey("meeting");

  const { canEdit, canDelete, canCreate } = useAcl("Meeting", meeting);

  const statusTranslations: Record<MeetingStatusKey, string> = {
    Planned: t("statuses.meeting.planned"),
    Held: t("statuses.meeting.held"),
    "Not Held": t("statuses.meeting.not_held"),
  };

  const loadAll = useCallback(async () => {
    if (!id || id === "new") return;
    setIsLoading(true);
    try {
      const m = await getMeetingById(id);
      const validStatus: MeetingStatusKey =
        m.status in meetingStatusColors
          ? (m.status as MeetingStatusKey)
          : "Planned";
      setMeeting(m);
      setForm({
        status: validStatus,
        assignedUserId: m.assignedUserId,
        assignedUserName: m.assignedUserName || defaultUserName,
        usersIds: m.usersIds || [],
        usersNames: m.usersNames || {},
        usersColumns: m.usersColumns || {},
        name: m.name,
        description: m.description || "",
        group: m.group || "",
        dateStart: m.dateStart || "",
        createdById: m.createdById || defaultUserId,
      });
      if (m.dateStart && m.dateEnd) {
        const start = dayjs(m.dateStart);
        const end = dayjs(m.dateEnd);
        const diffHours = end.diff(start, "hour", true);
        const closestDuration = DEFAULT_CONFIG.MEETING_DURATION_OPTIONS.find(
          (option) => Math.abs(option.value - diffHours) < 0.01,
        );
        setDuration(closestDuration ? String(closestDuration.value) : "1");
      }
    } catch (error) {
      console.error("Failed to load meeting:", error);
      setError(t("meetingDetail.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [id, defaultUserName, t, defaultUserId]);

  const initNewForm = useCallback(() => {
    setForm({
      status: "Planned",
      assignedUserId: defaultUserId,
      assignedUserName: defaultUserName,
      usersIds: [],
      usersNames: {},
      usersColumns: {},
      name: "",
      description: "",
      group: "",
      dateStart: "",
      createdById: defaultUserId,
    });
    setDuration("1");
    setIsEditing(true);
  }, [defaultUserId, defaultUserName]);

  useEffect(() => {
    let isMounted = true;
    let cancelled = false;

    const loadUsers = async () => {
      if (cancelled || !isMounted) return;
      setIsLoading(true);
      try {
        const { getCachedUsers } = await import("../storage/preloadData");
        const usersList = await getCachedUsers();
        if (isMounted && !cancelled) {
          setUsers(usersList);
        }
      } catch (error) {
        if (cancelled || !isMounted) return;
        console.error("Error loading users:", error);
        setError(t("meetingDetail.loadUsersError"));
      } finally {
        if (isMounted && !cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadUsers();
    loadAll();

    return () => {
      cancelled = true;
      isMounted = false;
    };
  }, [loadAll, t]);

  useEffect(() => {
    draftPromptShownRef.current = false;
    setDraftPromptOpen(false);
    setDraftToRestore(null);
  }, [id]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptShownRef.current) return;
    const run = async () => {
      const draft = await loadDraft<{ form: Partial<Meeting>; duration: string }>(
        draftKey,
      );
      if (!draft?.data) return;

      const hasContent =
        !!draft.data.form?.name ||
        !!draft.data.form?.description ||
        !!draft.data.form?.dateStart ||
        !!draft.data.form?.group ||
        (draft.data.form?.status && draft.data.form?.status !== "Planned");

      if (!hasContent) {
        await clearDraft(draftKey);
        return;
      }

      setDraftToRestore(draft.data);
      setDraftPromptOpen(true);
      draftPromptShownRef.current = true;
    };
    void run();
  }, [id, draftKey]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptOpen) return;
    const shouldSave =
      !!form.name ||
      !!form.description ||
      !!form.dateStart ||
      !!form.group ||
      (form.status && form.status !== "Planned");

    if (!shouldSave) {
      clearDraft(draftKey);
      return;
    }

    saveDraft(draftKey, { form, duration });
  }, [form, duration, id, draftKey, draftPromptOpen]);

  const handleUseDraft = () => {
    if (!draftToRestore) return;
    setForm((prev) => ({
      ...prev,
      ...draftToRestore.form,
      assignedUserId:
        draftToRestore.form.assignedUserId || prev.assignedUserId,
      assignedUserName:
        draftToRestore.form.assignedUserName || prev.assignedUserName,
      createdById: draftToRestore.form.createdById || prev.createdById,
    }));
    if (draftToRestore.duration) {
      setDuration(draftToRestore.duration);
    }
    setIsEditing(true);
    setDraftPromptOpen(false);
  };

  const handleStartNew = () => {
    clearDraft(draftKey);
    initNewForm();
    setDraftPromptOpen(false);
  };

  const handleTextChange =
    (field: keyof Partial<Meeting>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSelectChange =
    (field: keyof Partial<Meeting> | "duration") =>
    (e: SelectChangeEvent<string>) => {
      const newValue = e.target.value;
      if (field === "assignedUserId") {
        const selectedUser = users.find((user) => user.id === newValue);
        setForm((prev) => ({
          ...prev,
          assignedUserId: newValue,
          assignedUserName: selectedUser?.name || defaultUserName,
        }));
      } else if (field === "duration") {
        setDuration(newValue);
      } else {
        setForm((prev) => ({ ...prev, [field]: newValue }));
      }
    };

  const handleUpdateParticipants = ({
    usersIds,
    usersNames,
    usersColumns,
  }: {
    usersIds: string[];
    usersNames: Record<string, string>;
    usersColumns: Record<string, { status: string }>;
  }) => {
    setForm((prev) => ({
      ...prev,
      usersIds,
      usersNames,
      usersColumns,
    }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!form.name) {
      setError(t("meetingDetail.requiredName"));
      return;
    }
    if (!form.assignedUserId) {
      setError(t("meetingDetail.requiredAssignee"));
      return;
    }

    const startUtcIsoRaw = form.dateStart || "";
    if (!startUtcIsoRaw || isNaN(new Date(startUtcIsoRaw).getTime())) {
      setError(t("meetingDetail.invalidStartDate"));
      return;
    }

    const durationHours = parseFloat(duration || "1");
    if (!Number.isFinite(durationHours) || durationHours <= 0) {
      setError(t("meetingDetail.invalidEndDate"));
      return;
    }

    const startUtcIso = normalizeIsoUtc(startUtcIsoRaw);
    const endUtcIso = dayjs
      .utc(startUtcIso)
      .add(durationHours, "hour")
      .format("YYYY-MM-DDTHH:mm:ss[Z]");

    if (dayjs.utc(endUtcIso).isBefore(dayjs.utc(startUtcIso))) {
      setError(t("meetingDetail.endDateBeforeStart"));
      return;
    }

    if (id === "new" && !canCreate) {
      setError(t("meetingDetail.noCreatePermission"));
      return;
    }
    if (id !== "new" && !canEdit) {
      setError(t("meetingDetail.noEditPermission"));
      return;
    }

    const formattedForm: Partial<Meeting> = {
      ...form,
      dateStart: startUtcIso,
      dateEnd: endUtcIso,
    };

    setIsLoading(true);
    try {
      if (id === "new") {
        const created = await createMeeting(formattedForm);
        navigate(`/meeting/${created.id}`);
        setSuccess(t("meetingDetail.saveSuccess"));
        setIsEditing(false);
        clearDraft(draftKey);
      } else {
        await updateMeeting(id!, formattedForm);
        setIsEditing(false);
        setAnchorEl(null);
        setSuccess(t("meetingDetail.updateSuccess"));
      }
      await loadAll();
    } catch (error: any) {
      console.error("Error saving meeting:", error);
      const message =
        error.response?.data?.messageTranslation?.label === "validationFailure"
          ? t("meetingDetail.validationError", {
              field: error.response?.data?.messageTranslation?.data?.field,
            })
          : error.response?.data?.messageTranslation?.label ===
              "cannotRelateNonExisting"
            ? t("meetingDetail.nonExistingUser", {
                userId: form.assignedUserId,
              })
            : t("meetingDetail.saveError");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || id === "new") return;
    if (!canDelete) {
      setError(t("meetingDetail.noDeletePermission"));
      return;
    }
    setIsLoading(true);
    try {
      await deleteMeeting(id);
      setSuccess(t("meetingDetail.deleteSuccess"));
      setConfirmOpen(false);
      setTimeout(() => {
        navigate("/tasks");
      }, 1000);
    } catch (error) {
      console.error("Error deleting meeting:", error);
      setError(t("meetingDetail.deleteError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (id === "new") {
      navigate("/meetings");
    } else {
      setIsEditing(false);
      loadAll();
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenConfirm = () => {
    if (!canDelete) {
      setError(t("meetingDetail.noDeletePermission"));
      return;
    }
    setConfirmOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    await handleDelete();
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(null);
  };

  if (isLoading || (!meeting && id !== "new")) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language === "en_US" ? "en" : "ru"}
      localeText={
        i18n.language === "en_US"
          ? enUS.components.MuiLocalizationProvider.defaultProps.localeText
          : ruRU.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <Container sx={{ px: { xs: 0, sm: 2 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex" }}>
            <Button
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => navigate(-1)}
              sx={{
                textTransform: "none",
                px: "0",
                color: "#757575",
                minWidth: "auto",
              }}
              disabled={isLoading}
            ></Button>
            <Typography
              variant="h5"
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
            >
              {meeting?.name || t("meetingDetail.newMeeting")}
            </Typography>
          </Box>
          <Box
            sx={{
              transition: "opacity 0.3s ease, transform 0.3s ease",
              opacity: isEditing ? 0 : 1,
              transform: isEditing ? "translateY(-10px)" : "translateY(0)",
              pointerEvents: isEditing ? "none" : "auto",
            }}
          >
            {!isEditing && id !== "new" && (canEdit || canDelete) && (
              <IconButton onClick={handleOpenMenu} disabled={isLoading}>
                <MoreVertOutlinedIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            width: "100%",
            transition: "opacity 0.3s ease, transform 0.3s ease",
            opacity: isEditing ? 1 : 0,
            transform: isEditing ? "translateY(0)" : "translateY(10px)",
            pointerEvents: isEditing ? "auto" : "none",
            height: isEditing ? "auto" : 0,
            overflow: "hidden",
          }}
        >
          {isEditing && canEdit && (
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  textTransform: "none",
                  minWidth: { xs: "120px", sm: "150px" },
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                }}
                disabled={isLoading}
              >
                {t("meetingDetail.cancelButton")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  textTransform: "none",
                  minWidth: { xs: "120px", sm: "150px" },
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("meetingDetail.saveButton")
                )}
              </Button>
            </>
          )}
        </Box>
        {!isEditing && id !== "new" && (canEdit || canDelete) && (
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {canEdit && (
              <MenuItem
                onClick={() => {
                  setIsEditing(true);
                  setTimeout(() => handleCloseMenu(), 0);
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" sx={{ color: "#4467A5" }} />
                </ListItemIcon>
                <ListItemText primary={t("meetingDetail.editButton")} />
              </MenuItem>
            )}
            {canDelete && (
              <MenuItem onClick={handleOpenConfirm}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                </ListItemIcon>
                <ListItemText primary={t("meetingDetail.deleteButton")} />
              </MenuItem>
            )}
          </Popover>
        )}
        <ConfirmDialog
          open={confirmOpen}
          title={t("meetingDetail.deleteConfirmTitle")}
          content={t("meetingDetail.deleteConfirmContent")}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          loading={isLoading}
        />
        <Divider sx={{ mb: 2 }} />
        {isEditing || id === "new" ? (
          <Box component="form" noValidate sx={{ width: "100%" }}>
            <Box display="flex" flexDirection="column" gap={0}>
              <TextField
                label={t("meetingDetail.nameLabel")}
                fullWidth
                margin="normal"
                value={form.name || ""}
                onChange={handleTextChange("name")}
                disabled={!isEditing || !canEdit}
                size="small"
                required
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 0,
                }}
              >
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel
                    sx={{
                      color:
                        form.status &&
                        meetingStatusColors[form.status as MeetingStatusKey]
                          ? meetingStatusColors[form.status as MeetingStatusKey]
                              .color
                          : "inherit",
                      "&.Mui-focused": {
                        color:
                          form.status &&
                          meetingStatusColors[form.status as MeetingStatusKey]
                            ? meetingStatusColors[
                                form.status as MeetingStatusKey
                              ].color
                            : "inherit",
                      },
                    }}
                  >
                    {t("meetingDetail.statusLabel")}
                  </InputLabel>
                  <Select
                    label={t("meetingDetail.statusLabel")}
                    value={form.status || "Planned"}
                    onChange={handleSelectChange("status")}
                    disabled={!isEditing || !canEdit}
                    sx={{
                      color:
                        form.status &&
                        meetingStatusColors[form.status as MeetingStatusKey]
                          ? meetingStatusColors[form.status as MeetingStatusKey]
                              .color
                          : "inherit",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          form.status &&
                          meetingStatusColors[form.status as MeetingStatusKey]
                            ? meetingStatusColors[
                                form.status as MeetingStatusKey
                              ].color
                            : "inherit",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          form.status &&
                          meetingStatusColors[form.status as MeetingStatusKey]
                            ? meetingStatusColors[
                                form.status as MeetingStatusKey
                              ].color
                            : "inherit",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          form.status &&
                          meetingStatusColors[form.status as MeetingStatusKey]
                            ? meetingStatusColors[
                                form.status as MeetingStatusKey
                              ].color
                            : "inherit",
                      },
                      "&.Mui-disabled": {
                        color:
                          form.status &&
                          meetingStatusColors[form.status as MeetingStatusKey]
                            ? meetingStatusColors[
                                form.status as MeetingStatusKey
                              ].color
                            : "inherit",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            form.status &&
                            meetingStatusColors[form.status as MeetingStatusKey]
                              ? meetingStatusColors[
                                  form.status as MeetingStatusKey
                                ].color
                              : "inherit",
                        },
                      },
                    }}
                  >
                    {Object.keys(statusTranslations).map((status) => (
                      <MenuItem
                        key={status}
                        value={status}
                        sx={{
                          color:
                            meetingStatusColors[status as MeetingStatusKey]
                              .color,
                        }}
                      >
                        {statusTranslations[status as MeetingStatusKey]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>{t("meetingDetail.assigneeLabel")}</InputLabel>
                  <Select
                    label={t("meetingDetail.assigneeLabel")}
                    value={form.assignedUserId || defaultUserId}
                    onChange={handleSelectChange("assignedUserId")}
                    disabled={!isEditing || !canEdit}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 0,
                  alignItems: "flex-start",
                }}
              >
                <DateTimePicker
                  label={t("meetingDetail.startDateLabel")}
                  value={fromServerToLocal(form.dateStart)}
                  onChange={(newValue) =>
                    setForm((prev) => ({
                      ...prev,
                      dateStart: toServerUtcIsoStrict(newValue),
                    }))
                  }
                  ampm={false}
                  disabled={!isEditing || !canEdit}
                  reduceAnimations={true}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      size: "small",
                      error:
                        !!form.dateStart &&
                        isNaN(new Date(form.dateStart).getTime()),
                      helperText:
                        !!form.dateStart &&
                        isNaN(new Date(form.dateStart).getTime())
                          ? t("meetingDetail.invalidDate")
                          : "",
                    },
                    actionBar: {
                      actions: ["today", "cancel", "accept"],
                      sx: {
                        "& .MuiButton-root": {
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          color: "#fff",
                          minWidth: 80,
                        },
                        "& .MuiButton-root:nth-of-type(1)": {
                          // Сегодня
                          backgroundColor: "#4467A5",
                          "&:hover": {
                            backgroundColor: "#385894",
                          },
                        },
                        "& .MuiButton-root:nth-of-type(2)": {
                          // Отмена
                          backgroundColor: "#b71c1c",
                          "&:hover": {
                            backgroundColor: "#9a0007",
                          },
                        },
                        "& .MuiButton-root:nth-of-type(3)": {
                          // Сохранить
                          backgroundColor: "#2e7d32",
                          "&:hover": {
                            backgroundColor: "#1b5e20",
                          },
                        },
                      },
                    },
                  }}
                  sx={{ flex: 1 }}
                />

                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>{t("meetingDetail.durationLabel")}</InputLabel>
                  <Select
                    label={t("meetingDetail.durationLabel")}
                    value={duration}
                    onChange={handleSelectChange("duration")}
                    disabled={!isEditing || !canEdit}
                  >
                    {DEFAULT_CONFIG.MEETING_DURATION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={String(option.value)}>
                        {t(option.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label={t("meetingDetail.descriptionLabel")}
                fullWidth
                margin="normal"
                multiline
                minRows={3}
                value={form.description || ""}
                onChange={handleTextChange("description")}
                disabled={!isEditing || !canEdit}
                size="small"
              />
            </Box>
          </Box>
        ) : meeting ? (
          <MeetingView
            meeting={meeting}
            onStatusChange={loadAll}
            onStatusUpdateSuccess={(message) => setSuccess(message)}
          />
        ) : null}
        <Box sx={{ mt: 4 }}>
          <ParticipantsAccordion
            parentId={id || ""}
            usersIds={form.usersIds || []}
            usersNames={form.usersNames || {}}
            usersColumns={form.usersColumns || {}}
            onRefresh={loadAll}
            onUpdateForm={handleUpdateParticipants}
            createdById={meeting?.createdById || defaultUserId}
          />
        </Box>
        <Notification
          open={!!error}
          message={error || ""}
          severity="error"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <Notification
          open={!!success}
          message={success || ""}
          severity="success"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <DraftPromptDialog
          open={draftPromptOpen}
          onUseDraft={handleUseDraft}
          onStartNew={handleStartNew}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default MeetingDetail;
